import React, { useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ChevronLeft, 
  Loader2,
} from 'lucide-react';
import { projectAPI, createQueryKey, chatAPI } from '../services/api';
import FinishedProjectView from '../components/FinishedProjectView';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for active chat ID (for site projects)
  const [activeChatId, setActiveChatId] = React.useState(null);

  // Fetch project details - include refreshKey to force refetch when needed
  const { data: projectResponse, isLoading: projectLoading, error: projectError, refetch: refetchProject } = useQuery({
    queryKey: createQueryKey.project(projectId),
    queryFn: async () => {
      console.log('[ProjectDetailPage] Fetching project:', projectId);
      // Add cache-busting timestamp to ensure fresh data
      const response = await projectAPI.getById(projectId);
      // Handle both response structures: response.data (direct) or response.data.project (wrapped)
      const project = response.data?.project || response.data;
      console.log('[ProjectDetailPage] Project fetched:', {
        id: project?.id,
        name: project?.name,
        status: project?.status,
        statusType: typeof project?.status,
        settings: project?.settings,
        archetype: project?.settings?.archetype,
        responseStructure: response.data?.project ? 'wrapped' : 'direct',
        fullResponse: response
      });
      return response;
    },
    enabled: !!projectId && projectId !== 'undefined' && projectId !== 'null',
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: false, // Don't auto-refetch, but allow manual refetch
    staleTime: 0, // Always consider data stale to force refetch
    cacheTime: 0, // Don't cache - always fetch fresh
    // Don't retry on 404 - project doesn't exist
    retry: (failureCount, error) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
  
  // Listen for navigation events and force refetch if needed
  useEffect(() => {
    const handleFocus = () => {
      // Refetch when window gains focus (user might have updated project in another tab)
      refetchProject();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetchProject]);

  // Guard against invalid ID
  useEffect(() => {
    const isProjectDetailRoute = location.pathname.match(/^\/projects\/[^/]+$/);
    
    // Only redirect if we are actually trying to view a project detail page
    // AND the ID is missing. This prevents redirects during exit animations 
    // when navigating to pages like /discover
    if (isProjectDetailRoute && (!projectId || projectId === 'undefined' || projectId === 'null')) {
      console.warn('Invalid project ID detected:', projectId, 'Redirecting to projects page');
      navigate('/projects', { replace: true });
    }
  }, [projectId, navigate, location.pathname]);

  // Refetch project when status might have changed (listen for focus/visibility)
  useEffect(() => {
    if (!projectId || projectId === 'undefined' || projectId === 'null') return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[ProjectDetailPage] Page visible, refetching project');
        refetchProject();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [projectId, refetchProject]);

  // Handle response structure - backend returns ProjectResponse directly
  // axios wraps it in response.data, so it could be response.data (project object) or response.data.project
  const project = projectResponse?.data?.project || projectResponse?.data;
  const archetype = project?.settings?.archetype || 'event'; // Default to event for legacy support
  
  // Fetch active chat ID for site projects
  // NOTE: For Site projects, getMineForProject will return 404 if no active chat exists
  // This is intentional - Site projects require explicit "Start New Session" action
  useEffect(() => {
    if (project && project.settings?.archetype === 'site' && project.id) {
      chatAPI.getMineForProject(project.id)
        .then(response => {
          const chatId = response?.data?.data?.chatId;
          if (chatId) setActiveChatId(chatId);
        })
        .catch((error) => {
          // 404 means no active chat - this is expected and fine for Site projects
          // User must explicitly start a new session
          if (error?.response?.status === 404) {
            setActiveChatId(null);
          } else {
            // Other errors might be real issues, but we'll still set to null
            console.warn('[ProjectDetailPage] Error fetching active chat:', error);
            setActiveChatId(null);
          }
        });
    } else {
      setActiveChatId(null);
    }
  }, [project]);

  if (!projectId || projectId === 'undefined' || projectId === 'null') return null;

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (projectError || !project) {
    console.error('[ProjectDetailPage] Project fetch error:', {
      error: projectError,
      response: projectResponse,
      projectId,
      status: projectError?.response?.status,
      detail: projectError?.response?.data?.detail,
      responseData: projectResponse?.data
    });
    return (
      <div className="flex flex-col h-[calc(100vh-64px)]">
        <div className="text-center pt-20">
          <h1 className="text-2xl font-bold text-gray-900">Project not found</h1>
          <Link
            to="/projects"
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('[ProjectDetailPage] Rendering with:', {
    id: project.id,
    name: project.name,
    status: project.status,
    statusType: typeof project.status,
    statusValue: JSON.stringify(project.status),
    archetype,
    settings: project.settings,
    hasSettings: !!project.settings,
    settingsKeys: project.settings ? Object.keys(project.settings) : [],
    willShowLobby: archetype === 'event' && project.status === 'pending',
    willRedirect: archetype === 'event' && (project.status === 'ongoing' || project.status === 'finished')
  });

  // Unified view: Always use FinishedProjectView for both archetypes
  // FinishedProjectView handles:
  // - Site projects: Dashboard with sessions
  // - Event projects: Lobby (pending) or finished view (ongoing/finished)
  // - Event projects with ongoing/finished status: Redirects handled by FinishedProjectView or ChatPage
  return (
    <FinishedProjectView 
      project={project} 
      projectId={project.id}
      chatId={activeChatId}
      showBackLink={true}
    />
  );
};

export default ProjectDetailPage;
